import React, { useEffect, useMemo, useState } from 'react';
import { Button, CircularProgress, Dialog, DialogContent, DialogTitle } from '@material-ui/core';
import { fetcher } from '../../utils/axios';
import {
  assignPlaylistToCustomer,
  getPlaylistCustomerIds,
  removePlaylistFromCustomer,
} from '../../api/playlist';
import './PlaylistManagement.scss';

const PAGE_SIZE = 50;
const customerName = (customer) =>
  customer.name || [customer.firstname, customer.lastname].filter(Boolean).join(' ') || customer.username || 'Unnamed customer';

export default function PlaylistCustomerDialog({ playlist, open, onClose, onChange }) {
  const [customers, setCustomers] = useState([]);
  const [assignedIds, setAssignedIds] = useState([]);
  const [search, setSearch] = useState('');
  const [assignmentFilter, setAssignmentFilter] = useState('all');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [busyCustomerId, setBusyCustomerId] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !playlist?.id) return;
    let active = true;
    setSearch('');
    setAssignmentFilter('all');
    setVisibleCount(PAGE_SIZE);
    setError('');
    setCustomers([]);
    setAssignedIds([]);
    setLoading(true);
    Promise.all([fetcher('/customers/'), getPlaylistCustomerIds(playlist.id)])
      .then(([customerList, assignments]) => {
        if (!active) return;
        setCustomers(Array.isArray(customerList) ? customerList : []);
        setAssignedIds(assignments.customerIds || []);
      })
      .catch(() => {
        if (active) setError('Could not load customer assignments. Please try again.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [open, playlist?.id]);

  const toggleCustomer = async (customer) => {
    const assigned = assignedIds.includes(customer.id);
    setBusyCustomerId(customer.id);
    setError('');
    try {
      if (assigned) {
        await removePlaylistFromCustomer(playlist.id, customer.id);
        setAssignedIds((current) => current.filter((id) => id !== customer.id));
      } else {
        await assignPlaylistToCustomer(playlist.id, customer.id);
        setAssignedIds((current) => [...current, customer.id]);
      }
      if (onChange) onChange();
    } catch (err) {
      setError(`Could not ${assigned ? 'remove' : 'assign'} this playlist for ${customerName(customer)}.`);
    } finally {
      setBusyCustomerId(null);
    }
  };

  const assignedIdSet = useMemo(() => new Set(assignedIds), [assignedIds]);
  const assignedCustomerCount = useMemo(() => customers.filter((customer) => assignedIdSet.has(customer.id)).length, [customers, assignedIdSet]);
  const filteredCustomers = useMemo(() => {
    const term = search.trim().toLowerCase();
    return customers.filter((customer) => {
      const assigned = assignedIdSet.has(customer.id);
      if (assignmentFilter === 'assigned' && !assigned) return false;
      if (assignmentFilter === 'not-assigned' && assigned) return false;
      if (!term) return true;
      return [customerName(customer), customer.business_name, customer.email, customer.address, customer.phone, customer.username]
        .some((value) => String(value || '').toLowerCase().includes(term));
    });
  }, [customers, assignedIdSet, assignmentFilter, search]);

  return <Dialog className="playlist-management-dialog playlist-customer-dialog" open={open} onClose={busyCustomerId ? undefined : onClose} maxWidth={false}>
    <DialogTitle>Customers for {playlist?.name}</DialogTitle>
    <DialogContent>
      <p className="playlist-management-description">Search customer details, check assignments, and assign or remove this playlist.</p>
      <input
        className="playlist-management-search"
        type="search"
        value={search}
        onChange={(event) => { setSearch(event.target.value); setVisibleCount(PAGE_SIZE); }}
        placeholder="Search name, business, email, address, phone, or username"
        aria-label="Search customers by name, business, email, address, phone, or username"
      />
      <div className="playlist-customer-controls">
        <div className="playlist-copy-types" role="group" aria-label="Filter customers by playlist assignment">
          {[['all', 'All'], ['assigned', 'Assigned'], ['not-assigned', 'Not assigned']].map(([value, label]) => <button
            key={value}
            type="button"
            className={assignmentFilter === value ? 'active' : ''}
            aria-pressed={assignmentFilter === value}
            onClick={() => { setAssignmentFilter(value); setVisibleCount(PAGE_SIZE); }}
          >{label}</button>)}
        </div>
        <span>{loading ? 'Loading customers...' : `Showing ${filteredCustomers.length} of ${customers.length} customers · ${assignedCustomerCount} assigned`}</span>
      </div>
      {error && <p className="playlist-management-error" role="alert">{error}</p>}
      {loading ? <div className="playlist-management-loading"><CircularProgress size={24} /></div> :
        <div className="playlist-customer-table" role="group" aria-label="Customers and playlist assignments">
          <div className="playlist-customer-table-header">
            <span>Customer</span><span>Business</span><span>Email</span><span>Phone</span><span>Address</span><span>Status</span><span>Action</span>
          </div>
          {filteredCustomers.slice(0, visibleCount).map((customer) => {
            const assigned = assignedIdSet.has(customer.id);
            return <div className="playlist-customer-table-row" key={customer.id}>
              <div className="playlist-customer-name">
                <strong>{customerName(customer)}</strong>
                <small>{customer.username || '—'}</small>
              </div>
              <span>{customer.business_name || '—'}</span>
              <span>{customer.email || '—'}</span>
              <span>{customer.phone || '—'}</span>
              <span>{customer.address || '—'}</span>
              <span className={assigned ? 'playlist-assigned' : 'playlist-not-assigned'}>
                {assigned ? 'Assigned' : 'Not assigned'}
              </span>
              <Button
                color="primary"
                disabled={!!busyCustomerId}
                onClick={() => toggleCustomer(customer)}
                aria-label={`${assigned ? 'Remove' : 'Assign'} ${playlist?.name} ${assigned ? 'from' : 'to'} ${customerName(customer)}`}
              >
                {busyCustomerId === customer.id ? <CircularProgress size={18} /> : assigned ? 'Remove' : 'Assign'}
              </Button>
            </div>;
          })}
          {filteredCustomers.length === 0 && <p className="playlist-copy-empty">No customers match your search or filter.</p>}
          {visibleCount < filteredCustomers.length && <div className="playlist-copy-more"><Button onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}>Show more customers</Button></div>}
        </div>}
      <div className="playlist-management-footer"><Button onClick={onClose} disabled={!!busyCustomerId}>Close</Button></div>
    </DialogContent>
  </Dialog>;
}
